/* =============================================================================
   MASKHY — cart
   Shopify Ajax Cart API + Section Rendering API. Liquid renders the drawer (one source
   of markup, money formatting and translations); this file only moves data and swaps
   the rendered HTML in. Checkout is Shopify's own.

   MK.cart.add(items, { opener, label }) → Promise   items: [{ id, quantity }]
   MK.cart.open(opener) · MK.cart.close() · MK.cart.refresh()
   ============================================================================= */
(function () {
  'use strict';

  const MK = (window.MK = window.MK || {});
  const theme = window.MaskhyTheme || { routes: {}, strings: {} };
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const SECTION = 'cart-drawer';

  async function request(url, body) {
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname }),
      });
    } catch (e) {
      const error = new Error(strings.networkError || 'Network error');
      error.network = true;
      throw error;
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || strings.cartError || 'Cart error');
      error.data = data;
      throw error;
    }
    return data;
  }

  function updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => (el.textContent = String(count)));
    const label = (count === 1 ? strings.cartLinkOne : strings.cartLinkOther) || '';
    document.querySelectorAll('[data-cart-open]').forEach((el) => {
      if (label) el.setAttribute('aria-label', label.replace('{{ count }}', count));
    });
  }

  function bump() {
    document.querySelectorAll('.nav__cart').forEach((el) => {
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.opener = null;
      this.lineTimers = new Map();
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      });
      this.panel.addEventListener('transitionend', (e) => {
        if (e.target === this.panel && !this.classList.contains('is-open')) this.hidden = true;
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.hidden = false;
      void this.offsetWidth;
      this.classList.add('is-open');
      document.documentElement.classList.add('has-drawer');
      this.setInert(true);
      this.panel.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('has-drawer');
      this.setInert(false);
      this.notice('');
      if (!document.documentElement.classList.contains('motion-ok')) this.hidden = true;
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
    }

    trap(e) {
      const focusables = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    setInert(on) {
      const drawerSection = this.closest('.shopify-section') || this;
      Array.from(document.body.children).forEach((el) => {
        if (el === drawerSection || el.contains(this) || el.tagName === 'SCRIPT') return;
        if (on) {
          if (!el.inert) { el.inert = true; el.dataset.drawerInert = ''; }
        } else if (el.dataset.drawerInert !== undefined) {
          el.inert = false;
          delete el.dataset.drawerInert;
        }
      });
    }

    notice(text) {
      const el = this.querySelector('[data-drawer-notice]');
      if (!el) return;
      el.querySelector('[data-drawer-notice-text]').textContent = text || '';
      el.hidden = !text;
    }

    render(sectionHtml, { highlightKey, notice } = {}) {
      if (!sectionHtml) return;
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const nextPanel = doc.querySelector('[data-drawer-panel]');
      const next = doc.querySelector('[data-cart-drawer]');
      if (!nextPanel || !next) return;
      const focused = document.activeElement && document.activeElement.closest('[data-line]');
      const focusSelector = focused ? `[data-key="${CSS.escape(focused.dataset.key)}"] [data-line-qty]` : null;
      this.panel.innerHTML = nextPanel.innerHTML;
      this.dataset.itemCount = next.dataset.itemCount;
      updateCount(Number(next.dataset.itemCount || 0));
      if (notice) this.notice(notice);
      if (highlightKey) {
        const line = this.querySelector(`[data-key="${CSS.escape(highlightKey)}"]`);
        if (line) line.classList.add('is-new');
      }
      if (this.isOpen) {
        const restore = focusSelector && this.querySelector(focusSelector);
        (restore || this.panel).focus({ preventScroll: true });
      }
    }

    onClick(e) {
      if (e.target.closest('[data-drawer-close]')) {
        const link = e.target.closest('a[href]');
        this.close();
        if (link && link.hash && link.pathname === window.location.pathname) return;
        if (!link) e.preventDefault();
        return;
      }
      const step = e.target.closest('[data-line-step]');
      if (step) {
        const line = step.closest('[data-line]');
        const input = line.querySelector('[data-line-qty]');
        const next = Math.max(0, Number(input.value || 0) + Number(step.dataset.lineStep));
        input.value = next;
        this.queueChange(line, next);
        return;
      }
      const remove = e.target.closest('[data-line-remove]');
      if (remove) this.changeLine(remove.closest('[data-line]'), 0);
    }

    onChange(e) {
      const input = e.target.closest('[data-line-qty]');
      if (!input) return;
      this.queueChange(input.closest('[data-line]'), Math.max(0, Number(input.value || 0)));
    }

    queueChange(line, quantity) {
      const key = line.dataset.key;
      clearTimeout(this.lineTimers.get(key));
      this.lineTimers.set(key, setTimeout(() => this.changeLine(line, quantity), 320));
    }

    async changeLine(line, quantity) {
      const key = line.dataset.key;
      this.lineTimers.delete(key);
      line.classList.add('is-updating');
      this.setError('');
      try {
        const data = await request(`${routes.cartChange || '/cart/change'}.js`, { id: key, quantity });
        this.render(data.sections && data.sections[SECTION]);
      } catch (error) {
        const data = error.data || {};
        if (data.sections && data.sections[SECTION]) this.render(data.sections[SECTION]);
        else line.classList.remove('is-updating');
        this.setError(error.message);
      }
    }

    setError(message) {
      const el = this.querySelector('[data-drawer-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }
  }

  if (!customElements.get('mk-cart-drawer')) customElements.define('mk-cart-drawer', CartDrawer);

  const drawer = () => document.querySelector('mk-cart-drawer');

  function toast(text) {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    clearTimeout(el.__t);
    el.__t = setTimeout(() => (el.hidden = true), 2600);
  }

  MK.cart = {
    async add(items, { opener } = {}) {
      const data = await request(`${routes.cartAdd || '/cart/add'}.js`, { items });
      const el = drawer();
      const added = (data.items || [])[0];
      const notice = (strings.addedToast || 'MASKHY added').toUpperCase();
      if (el) {
        el.render(data.sections && data.sections[SECTION], { highlightKey: added && added.key, notice });
        el.open(opener);
      } else {
        toast(notice);
      }
      bump();
      return data;
    },
    open(opener) {
      const el = drawer();
      if (el) el.open(opener);
    },
    close() {
      const el = drawer();
      if (el) el.close();
    },
    async refresh() {
      const response = await fetch(`${window.location.pathname}?sections=${SECTION}`);
      const data = await response.json();
      const el = drawer();
      if (el) el.render(data[SECTION]);
    },
  };

  // The cart link opens the drawer; without JavaScript it goes to /cart.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-open]');
    if (!trigger || !drawer()) return;
    e.preventDefault();
    MK.cart.open(trigger);
  });

  // Back/forward cache can restore a stale cart.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) MK.cart.refresh().catch(() => {});
  });
})();
